import React, { useEffect, useMemo, useRef, useState } from "react"

import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons"

import { PayerStatus } from "@common-shared/config/oneVerify/oneVerifyPayerStatus.config"
import type {
    IOneVerifyPayerStatus,
    IOneVerifyPayerStatusIncident,
} from "@common-shared/types/responseData/oneVerifyResponseData.types"

import { ButtonElement } from "@front-shared/elements/button.element"
import { PageHelmetElement } from "@front-shared/elements/pageHelmet.element"
import { PrimaryText } from "@front-shared/elements/primaryText.element"
import { useIsMobile } from "@front-shared/hooks/isMobile.hook"

import { GENERAL_CONFIG } from "../../config/general.config"
import { PAYER_STATUS_CONFIG } from "../../config/payerStatus.config"
import { RuntimeHelper } from "../../helpers/runtime.helper"
import { UtilHelper } from "../../helpers/util.helper"
import { useAppDispatch } from "../../store/appDispatch.hook"
import { useAppSelector } from "../../store/selectors/app.selector"
import { runtimeSelector } from "../../store/selectors/runtime.selector"
import { payerStatusGet } from "../../store/thunks/payerStatus.thunks"
import { PayerStatusDrawerComponent } from "./payerStatusDrawer.component"
import { PayerStatusFooterComponent } from "./payerStatusFooter.component"
import { PayerStatusListComponent } from "./payerStatusList.component"
import { PayerStatusLoadingComponent } from "./payerStatusLoading.component"
import { PayerStatusMessageComponent } from "./payerStatusMessage.component"
import { PayerStatusStaleBannerComponent } from "./payerStatusStaleBanner.component"
import { PayerStatusSummaryComponent } from "./payerStatusSummary.component"
import { PayerStatusTimelineComponent } from "./payerStatusTimeline.component"

export const PayerStatusComponent: React.FunctionComponent = () => {
    const dispatch = useAppDispatch()
    const runtimeState = useAppSelector(runtimeSelector)

    const isMobile = useIsMobile()

    const timelineRef = useRef<HTMLDivElement>(null)

    const listRef = useRef<HTMLDivElement>(null)

    // Background refresh is skipped while previous request is still running, e.g. when Datadog is slow
    const pendingRequestsRef = useRef<number>(0)

    // Set from summary cards or from payer list
    const [statusFilter, setStatusFilter] = useState<PayerStatus | null>(null)

    const [payerStatus, setPayerStatus] =
        useState<IOneVerifyPayerStatus | null>(null)

    // When last request failed, previous data stays on screen, set on each failure so time since update stays current
    const [failedAt, setFailedAt] = useState<number | null>(null)

    // Payer stays selected while drawer closes, so its content doesn't disappear
    const [selectedPayerId, setSelectedPayerId] = useState<string | null>(null)

    const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false)

    const isLoading = useMemo<boolean>(() => {
        return RuntimeHelper.isPayerStatusLoading()
    }, [runtimeState.isLoading])

    const isError = !payerStatus && !!failedAt && !isLoading

    const isStale =
        !!payerStatus &&
        !!failedAt &&
        failedAt - payerStatus.fetchedAt >= PAYER_STATUS_CONFIG.staleAfterMs

    const selectedPayerIncidents = useMemo<
        IOneVerifyPayerStatusIncident[]
    >(() => {
        return (payerStatus?.incidents || []).filter(
            incident => incident.payerId === selectedPayerId,
        )
    }, [payerStatus, selectedPayerId])

    /**
     * Get payer status, refresh in background keeps current data on screen without loading state
     */
    const loadPayerStatus = (isBackground = false) => {
        if (isBackground && pendingRequestsRef.current) {
            return
        }

        // Request would fail with a toast on every refresh, data still goes out of date
        if (isBackground && !navigator.onLine) {
            setFailedAt(Date.now())

            return
        }

        pendingRequestsRef.current++

        dispatch(
            payerStatusGet({
                payload: null,
                ignorePreloader: isBackground,
                onSuccess: (result: IOneVerifyPayerStatus) => {
                    setPayerStatus(result)
                    setFailedAt(null)
                },
                onError: () => {
                    setFailedAt(Date.now())
                },
                onFinally: () => {
                    pendingRequestsRef.current--
                },
            }),
        )
    }

    useEffect(() => {
        loadPayerStatus()

        const intervalId = setInterval(() => {
            loadPayerStatus(true)
        }, PAYER_STATUS_CONFIG.refreshIntervalSeconds * 1000)

        return () => {
            clearInterval(intervalId)
        }
    }, [])

    const onPayerClick = (payerId: string) => {
        setSelectedPayerId(payerId)
        setIsDrawerOpen(true)
    }

    const renderContent = (): React.ReactNode => {
        if (!payerStatus) {
            return isError ? (
                <PayerStatusMessageComponent
                    role="alert"
                    icon={faTriangleExclamation}
                    title="We couldn’t load payer status"
                    text="This is a problem on our side, not with your payers. Try again in a moment, or contact support if you need help now."
                >
                    <ButtonElement
                        htmlType="button"
                        type="primary"
                        size="middle"
                        label="Try again"
                        onClick={() => {
                            loadPayerStatus()
                        }}
                    />

                    <ButtonElement
                        htmlType="button"
                        type="default"
                        size="middle"
                        label="Contact support"
                        onClick={UtilHelper.openSupportEmail}
                    />
                </PayerStatusMessageComponent>
            ) : (
                <PayerStatusLoadingComponent />
            )
        }

        return (
            <>
                {isStale && (
                    <PayerStatusStaleBannerComponent
                        fetchedAt={payerStatus.fetchedAt}
                        failedAt={failedAt}
                        isLoading={isLoading}
                        onRefresh={() => {
                            loadPayerStatus()
                        }}
                    />
                )}

                <PayerStatusSummaryComponent
                    payers={payerStatus.payers}
                    fetchedAt={payerStatus.fetchedAt}
                    isLoading={isLoading}
                    isStale={isStale}
                    statusFilter={statusFilter}
                    onStatusFilterChange={status => {
                        setStatusFilter(status)

                        // Filtered list is shown, clearing filter keeps page where it is
                        if (status) {
                            listRef.current?.scrollIntoView({
                                behavior: "smooth",
                            })
                        }
                    }}
                    onAffectedClick={() => {
                        timelineRef.current?.scrollIntoView({
                            behavior: "smooth",
                        })
                    }}
                />

                <div ref={timelineRef}>
                    <PayerStatusTimelineComponent
                        incidents={payerStatus.incidents}
                        fetchedAt={payerStatus.fetchedAt}
                        onPayerClick={onPayerClick}
                    />
                </div>

                <div ref={listRef}>
                    <PayerStatusListComponent
                        payers={payerStatus.payers}
                        incidents={payerStatus.incidents}
                        fetchedAt={payerStatus.fetchedAt}
                        statusFilter={statusFilter}
                        onStatusFilterChange={setStatusFilter}
                        onPayerClick={onPayerClick}
                    />
                </div>

                <PayerStatusDrawerComponent
                    isOpen={isDrawerOpen}
                    payer={
                        payerStatus.payers.find(
                            row => row.payerId === selectedPayerId,
                        ) || null
                    }
                    incidents={selectedPayerIncidents}
                    fetchedAt={payerStatus.fetchedAt}
                    onClose={() => {
                        setIsDrawerOpen(false)
                    }}
                />
            </>
        )
    }

    return (
        <div className="relative mx-auto flex w-full max-w-[1180px] flex-col gap-[32px] pb-32">
            <PageHelmetElement
                title="Payer Status"
                defaultPageTitle={GENERAL_CONFIG.defaultPageTitle}
            />

            <PrimaryText
                typography={isMobile ? "h2" : "h1"}
                className="mt-[32px] sm:mt-[64px]"
            >
                Payer Status
            </PrimaryText>

            {renderContent()}

            <PayerStatusFooterComponent />
        </div>
    )
}
